// Pannello di sola lettura con una conversazione cliente↔consulente completa.
// Lo usano sia "Conversazioni" (dall'elenco) sia "Messaggi preferiti" (da un
// messaggio stellato, che viene evidenziato e portato a fuoco).
import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Spinner } from '@klaro7/ui';
import type { ChatParticipantRole } from '@klaro7/shared-types';

import { StarIcon } from '@/features/chat/components/StarIcon';
import { useAdminConversation } from '@/features/chat/hooks/useStarredMessages';
import { formatDateTime, roleLabel } from '@/features/chat/lib/adminChat';

interface Props {
  conversationId: string;
  /** Messaggio da evidenziare, quando si arriva da un preferito. */
  highlightMessageId?: string;
  onClose: () => void;
}

export function AdminConversationDrawer({ conversationId, highlightMessageId, onClose }: Props) {
  const { data, isLoading, isError } = useAdminConversation(conversationId);
  const targetRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!data) return;
    // Da un preferito si va sul messaggio; dall'elenco si parte dalla fine.
    if (highlightMessageId) targetRef.current?.scrollIntoView({ block: 'center' });
    else bottomRef.current?.scrollIntoView({ block: 'end' });
  }, [data, highlightMessageId]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const starredBy = new Map<string, ChatParticipantRole[]>();
  for (const s of data?.starred ?? []) {
    starredBy.set(s.messageId, [...(starredBy.get(s.messageId) ?? []), s.by]);
  }

  return (
    <div className="fixed inset-0 z-50 flex justify-end" role="dialog" aria-modal="true" aria-label="Conversazione">
      <button type="button" aria-label="Chiudi" className="absolute inset-0 bg-neutral-900/40" onClick={onClose} />
      <div className="relative flex h-full w-full max-w-xl flex-col bg-white shadow-2xl">
        <div className="flex items-start justify-between gap-4 border-b border-neutral-200 px-5 py-4">
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-neutral-900">Conversazione</h2>
            {data && (
              <p className="mt-0.5 truncate text-sm text-neutral-500">
                <Link
                  to={`/dashboard/admin/users/${data.conversation.user.id}`}
                  className="text-brand-teal hover:underline"
                >
                  {data.conversation.user.name}
                </Link>{' '}
                ↔{' '}
                <Link
                  to={`/dashboard/admin/professionals/${data.conversation.professional.id}`}
                  className="text-brand-teal hover:underline"
                >
                  {data.conversation.professional.name}
                </Link>
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg px-2 py-1 text-sm font-medium text-neutral-500 hover:bg-neutral-100 hover:text-neutral-900"
          >
            Chiudi
          </button>
        </div>

        <div className="flex-1 overflow-y-auto bg-neutral-50 px-4 py-4">
          {isLoading && (
            <div className="flex justify-center py-16">
              <Spinner size="lg" />
            </div>
          )}
          {isError && <p className="py-16 text-center text-sm text-neutral-500">Conversazione non disponibile.</p>}
          {data?.truncated && (
            <p className="mb-3 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-700">
              Conversazione molto lunga: sono mostrati solo i messaggi più recenti.
            </p>
          )}
          {data?.messages.map((m) => {
            const isPro = m.senderRole === 'professional';
            const stars = starredBy.get(m.id);
            const isTarget = m.id === highlightMessageId;
            return (
              <div
                key={m.id}
                ref={isTarget ? targetRef : undefined}
                className={`mt-2 flex ${isPro ? 'justify-end' : 'justify-start'}`}
              >
                <div className={`flex max-w-[85%] flex-col ${isPro ? 'items-end' : 'items-start'}`}>
                  <div
                    className={`rounded-2xl px-3.5 py-2 text-sm ${
                      isPro ? 'bg-brand-teal text-white' : 'border border-neutral-200 bg-white text-neutral-900'
                    } ${isTarget ? 'ring-2 ring-brand-orange ring-offset-2' : stars ? 'ring-1 ring-brand-orange/60' : ''}`}
                  >
                    <p className="whitespace-pre-wrap break-words">{m.content}</p>
                  </div>
                  <div className="mt-0.5 flex items-center gap-1.5 px-1 text-[11px] text-neutral-400">
                    <span>
                      {isPro ? data.conversation.professional.name : data.conversation.user.name} ·{' '}
                      {formatDateTime(m.createdAt)}
                    </span>
                    {stars && (
                      <span className="inline-flex items-center gap-0.5 font-medium text-brand-orange">
                        <StarIcon filled className="h-3 w-3" />
                        {stars.map((r) => roleLabel[r]).join(' e ')}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
          <div ref={bottomRef} />
        </div>

        <p className="border-t border-neutral-200 bg-white px-5 py-3 text-[11px] text-neutral-400">
          Sola lettura. L'apertura di questa conversazione è registrata nello storico attività di cliente e
          consulente.
        </p>
      </div>
    </div>
  );
}
